import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Clock, Baby, Lock, Gamepad2, ShoppingBag, Users, KeyRound, Package, RotateCcw, ArrowDownLeft, ArrowUpRight, Crown, LayoutDashboard, Star, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from "recharts";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import SubscriptionPlans from "../components/SubscriptionPlans";
import GiftPanel from "../components/GiftPanel";
import InvitePanel from "../components/InvitePanel";
import ChangePassword from "../components/ChangePassword";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const LEARNING = [
  { day: "Mon", min: 35 }, { day: "Tue", min: 50 }, { day: "Wed", min: 28 },
  { day: "Thu", min: 62 }, { day: "Fri", min: 44 }, { day: "Sat", min: 75 }, { day: "Sun", min: 40 },
];
const COLORS = ["#FF5A79", "#FFC02D", "#2AB7CA", "#2EC4B6", "#9B5DE5", "#F15BB5", "#FF5A79"];

function Card({ children, className = "", ...rest }) {
  return <div className={`bg-white rounded-3xl border border-amber-100/60 shadow-lg p-6 ${className}`} {...rest}>{children}</div>;
}

export default function ParentDashboard() {
  const { loadSettings, refreshSubscription, user } = useAuth();
  const navigate = useNavigate();
  const [kids, setKids] = useState([]);
  const [settings, setSettings] = useState(null);
  const [limit, setLimit] = useState([90]);
  const [orders, setOrders] = useState({ purchases: [], sales: [] });
  const [usages, setUsages] = useState({});
  const [watchlists, setWatchlists] = useState({});
  const [pin, setPin] = useState("");
  const [sub, setSub] = useState({ subscription: null, status: "none", renewal: null, cancel_at_period_end: false });
  const [plansOpen, setPlansOpen] = useState(false);
  const [savedCentres, setSavedCentres] = useState([]);

  const loadSub = () => api.get("/subscription").then(({ data }) => setSub(data));

  const cancelPlan = async () => {
    try { const { data } = await api.post("/subscription/cancel"); setSub(data); refreshSubscription(); toast.success("Subscription updated"); }
    catch { toast.error("Could not cancel"); }
  };

  const loadKids = async () => {
    const { data } = await api.get("/profiles");
    setKids(data);
    const u = {};
    const wl = {};
    await Promise.all(data.map(async (k) => {
      try { const r = await api.get(`/profiles/${k.profile_id}/usage`); u[k.profile_id] = r.data; } catch (e) { console.error("load usage failed", e); }
      try { const w = await api.get(`/profiles/${k.profile_id}/watchlist`); wl[k.profile_id] = w.data; } catch (e) { console.error("load watchlist failed", e); }
    }));
    setUsages(u);
    setWatchlists(wl);
  };

  useEffect(() => {
    loadKids();
    api.get("/settings").then(({ data }) => { setSettings(data); setLimit([data.daily_limit]); });
    api.get("/orders").then(({ data }) => setOrders(data));
    api.get("/childcare/saved").then(({ data }) => setSavedCentres(data)).catch(() => {});
    loadSub();
  }, []);

  const save = async (patch) => {
    const next = { ...settings, ...patch };
    delete next.has_pin;
    setSettings({ ...settings, ...patch });
    try { await api.put("/settings", next); loadSettings(); toast.success("Setting saved 🛡️"); }
    catch { toast.error("Could not save"); }
  };
  const flip = (k) => save({ [k]: !settings[k] });

  const savePin = async () => {
    try { await api.put("/settings/pin", { pin }); setPin(""); toast.success("PIN updated 🔒"); }
    catch (e) { toast.error(e.response?.data?.detail || "PIN must be 4-6 digits"); }
  };

  const resetUsage = async (id) => {
    try { const { data } = await api.post(`/profiles/${id}/reset-usage`); setUsages((u) => ({ ...u, [id]: data })); toast.success("Screen time reset ⏱️"); }
    catch { toast.error("Could not reset"); }
  };

  const totalMin = LEARNING.reduce((a, b) => a + b.min, 0);
  if (!settings) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-[#FFFDF7] pb-16">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-2xl bg-[#2EC4B6] text-white flex items-center justify-center"><ShieldCheck /></span>
            <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">Parent Zone</h1>
          </div>
          {user?.is_admin && (
            <Button data-testid="open-admin-portal-btn" onClick={() => navigate("/admin")}
              className="rounded-full font-bold bg-slate-900 hover:bg-slate-800 text-white h-10 px-4 shrink-0">
              <LayoutDashboard className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Admin Portal</span>
            </Button>
          )}
        </div>
        <p className="text-slate-500 mb-8">Keep Bubz safe, balanced and fun for your family.</p>

        <div className="mb-6 rounded-3xl bg-[#2EC4B6]/10 border border-[#2EC4B6]/30 p-5 flex items-start gap-3" data-testid="adults-only-banner">
          <Lock className="h-5 w-5 text-[#2EC4B6] mt-0.5 shrink-0" />
          <div>
            <div className="font-bold text-slate-800">Marketplace &amp; Family Feed are adults-only</div>
            <div className="text-sm text-slate-500">Kid profiles can only access Watch and Games. Buying, selling and the social feed are never shown to children.</div>
          </div>
          <div className="ml-auto hidden sm:flex gap-2">
            <span className="h-9 w-9 rounded-xl bg-white flex items-center justify-center text-[#FFC02D]"><ShoppingBag className="h-5 w-5" /></span>
            <span className="h-9 w-9 rounded-xl bg-white flex items-center justify-center text-[#2AB7CA]"><Users className="h-5 w-5" /></span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Childcare directory */}
          <Card className="lg:col-span-3 flex flex-col gap-4" data-testid="childcare-card">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer" onClick={() => navigate("/childcare")}>
              <span className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#2EC4B6] to-[#2AB7CA] text-white flex items-center justify-center shrink-0"><Baby className="h-6 w-6" /></span>
              <div className="flex-1">
                <h2 className="text-xl font-display font-bold text-slate-900">Childcare Directory</h2>
                <p className="text-sm text-slate-500">Browse childcare centres across Australia — search by suburb, see fees, ages and vacancies.</p>
              </div>
              <Button data-testid="open-childcare-btn" onClick={(e) => { e.stopPropagation(); navigate("/childcare"); }}
                className="rounded-full font-bold bg-[#2EC4B6] hover:bg-[#25a99d] text-white shrink-0">Open directory</Button>
            </div>
            {savedCentres.length > 0 && (
              <div className="border-t border-slate-100 pt-4" data-testid="saved-centres">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Star className="h-3.5 w-3.5 fill-[#FFC02D] text-[#FFC02D]" /> Saved centres ({savedCentres.length})</div>
                <div className="flex flex-wrap gap-2">
                  {savedCentres.map((c) => (
                    <button key={c.id} onClick={() => navigate("/childcare")} data-testid={`saved-centre-${c.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-3 py-1.5 text-sm font-semibold hover:bg-amber-100">
                      {c.name} <span className="text-amber-400">· {c.suburb}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Bubz + subscription */}
          <Card className="lg:col-span-3 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="subscription-card">
            <span className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#FF5A79] to-[#9B5DE5] text-white flex items-center justify-center shrink-0"><Crown className="h-6 w-6" /></span>
            <div className="flex-1">
              <h2 className="text-xl font-display font-bold text-slate-900">Bubz <span className="text-[#FF5A79]">+</span> Subscription</h2>
              <p className="text-sm text-slate-500">
                {sub.subscription
                  ? <>Current plan: <span className="font-bold text-slate-700 capitalize">{sub.subscription}</span>
                      {sub.source === "gift"
                        ? <span className="text-[#9B5DE5] font-semibold"> · 🎁 gift{sub.renewal ? ` · expires ${new Date(sub.renewal).toLocaleDateString()}` : ""}</span>
                        : sub.cancel_at_period_end
                        ? <span className="text-amber-600"> · cancels on {sub.renewal ? new Date(sub.renewal).toLocaleDateString() : "period end"}</span>
                        : sub.renewal ? <span> · renews {new Date(sub.renewal).toLocaleDateString()}</span> : <span> · active</span>}</>
                  : "No plan yet. Choose a streaming tier, or start free with the Demo tier."}
              </p>
            </div>
            <div className="flex gap-2">
              <Button data-testid="manage-plan-btn" onClick={() => setPlansOpen(true)}
                className="rounded-full bg-[#FF5A79] hover:bg-[#e04865] font-bold px-6 h-11">
                {sub.subscription ? "Change plan" : "Choose a plan"}
              </Button>
              {sub.subscription && !sub.cancel_at_period_end && (
                <Button data-testid="cancel-plan-btn" onClick={cancelPlan} variant="outline" className="rounded-full font-bold px-5 h-11 border-2">
                  Cancel
                </Button>
              )}
            </div>
          </Card>

          <GiftPanel />

          <Card className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-bold text-slate-900">Learning this week</h2>
              <span className="rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-sm font-bold">{totalMin} min total</span>
            </div>
            <div className="h-56" data-testid="learning-chart">
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <BarChart data={LEARNING}>
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: "#94A3B8", fontSize: 12 }} />
                  <Bar dataKey="min" radius={[10, 10, 10, 10]}>
                    {LEARNING.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-2 mb-4"><Clock className="h-5 w-5 text-[#FF5A79]" /><h2 className="text-xl font-display font-bold text-slate-900">Daily limit</h2></div>
            <div className="text-4xl font-display font-bold text-[#FF5A79] mb-4">{limit[0]} <span className="text-lg text-slate-400">min/day</span></div>
            <Slider data-testid="screen-time-slider" value={limit} onValueChange={setLimit}
              onValueCommit={(v) => save({ daily_limit: v[0] })} min={15} max={240} step={15} />
            <div className="flex justify-between text-xs text-slate-400 mt-2"><span>15m</span><span>4h</span></div>
            <p className="text-sm text-slate-500 mt-4">Kids see a gentle lock screen when the day's minutes run out.</p>
          </Card>

          <Card className="lg:col-span-2">
            <h2 className="text-xl font-display font-bold text-slate-900 mb-1">Streaming safety</h2>
            <p className="text-sm text-slate-500 mb-4">These control what shows your kids can watch.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                ["ageLock", "Age-appropriate lock", "Only show shows matching each kid's age", Lock],
                ["educationalOnly", "Educational only mode", "Hide cartoons, keep learning content", Gamepad2],
              ].map(([key, title, desc, Icon]) => (
                <div key={key} className="flex items-start gap-3 rounded-2xl bg-slate-50 p-4">
                  <span className="h-9 w-9 rounded-xl bg-white flex items-center justify-center text-[#2AB7CA] shrink-0"><Icon className="h-5 w-5" /></span>
                  <div className="flex-1">
                    <div className="font-bold text-slate-800 text-sm">{title}</div>
                    <div className="text-xs text-slate-500">{desc}</div>
                  </div>
                  <Switch data-testid={`toggle-${key}`} checked={!!settings[key]} onCheckedChange={() => flip(key)} />
                </div>
              ))}
            </div>
          </Card>

          {/* Parent PIN */}
          <Card>
            <div className="flex items-center gap-2 mb-2"><KeyRound className="h-5 w-5 text-[#9B5DE5]" /><h2 className="text-xl font-display font-bold text-slate-900">Parent PIN</h2></div>
            <p className="text-sm text-slate-500 mb-4">Kids need this PIN to leave their profile or open the Parent Zone.</p>
            <Input data-testid="pin-change-input" inputMode="numeric" value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="New 4-6 digit PIN" className="rounded-xl h-11 tracking-widest" />
            <Button data-testid="save-pin-btn" onClick={savePin} disabled={pin.length < 4}
              className="mt-3 w-full h-11 rounded-xl bg-[#9B5DE5] hover:bg-[#8a4fd0] font-bold">Update PIN</Button>
            <p className="text-xs text-slate-400 mt-2">Current demo PIN: 1234</p>
          </Card>

          <Card className="lg:col-span-3" data-testid="invite-card">
            <div className="flex items-center gap-2 mb-2"><Users className="h-5 w-5 text-[#FF5A79]" /><h2 className="text-xl font-display font-bold text-slate-900">Invite a family</h2></div>
            <p className="text-sm text-slate-500 mb-4">Bubz + is invite-only. Send a private link so friends and family can join.</p>
            <InvitePanel />
          </Card>

          <Card className="lg:col-span-3" data-testid="password-card">
            <div className="flex items-center gap-2 mb-2"><KeyRound className="h-5 w-5 text-[#9B5DE5]" /><h2 className="text-xl font-display font-bold text-slate-900">Password &amp; security</h2></div>
            <p className="text-sm text-slate-500 mb-4">Change the password you use to sign in to Bubz +.</p>
            <ChangePassword />
          </Card>

          <Card className="lg:col-span-3" data-testid="legal-card">
            <div className="flex items-center gap-2 mb-2"><FileText className="h-5 w-5 text-[#2AB7CA]" /><h2 className="text-xl font-display font-bold text-slate-900">Legal &amp; privacy</h2></div>
            <p className="text-sm text-slate-500 mb-4">How we protect your family across streaming, Playground, Marketplace and messages.</p>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" data-testid="parent-terms-link" onClick={() => navigate("/terms")} className="rounded-xl font-bold">Terms &amp; Conditions</Button>
              <Button variant="outline" data-testid="parent-privacy-link" onClick={() => navigate("/privacy")} className="rounded-xl font-bold">Privacy Policy</Button>
            </div>
          </Card>

          {/* Kids + screen time */}
          <Card className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-4"><Baby className="h-5 w-5 text-[#FFC02D]" /><h2 className="text-xl font-display font-bold text-slate-900">Your kids &amp; today's screen time</h2></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kids-list">
              {kids.map((k) => {
                const u = usages[k.profile_id];
                const pct = u ? Math.min(100, Math.round((u.used / u.limit) * 100)) : 0;
                return (
                  <div key={k.profile_id} className="rounded-2xl bg-slate-50 p-4">
                    <div className="flex items-center gap-3">
                      <span className="h-11 w-11 rounded-xl flex items-center justify-center text-2xl" style={{ backgroundColor: k.avatar_color + "22" }}>{k.avatar_emoji}</span>
                      <div className="flex-1">
                        <div className="font-bold text-slate-800">{k.name} <span className="text-xs font-normal text-slate-400">· Age {k.age}</span></div>
                        {k.stickers?.length > 0 && <div className="text-xs font-bold text-amber-500">⭐ {k.stickers.length} stickers</div>}
                      </div>
                    </div>
                    {u && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span>{u.used} / {u.limit} min used</span>
                          <span className={u.remaining <= 10 ? "text-rose-500 font-bold" : ""}>{u.remaining}m left</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? "#f43f5e" : "#2EC4B6" }} />
                        </div>
                        <Button data-testid={`reset-usage-${k.profile_id}`} onClick={() => resetUsage(k.profile_id)}
                          variant="outline" className="mt-3 w-full h-9 rounded-xl text-sm font-semibold border-2">
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset today
                        </Button>
                      </div>
                    )}
                    {(watchlists[k.profile_id]?.length > 0) && (
                      <div className="mt-3 pt-3 border-t border-slate-200" data-testid={`watchlist-review-${k.profile_id}`}>
                        <div className="text-xs font-bold text-slate-500 mb-1">⭐ Watch later ({watchlists[k.profile_id].length})</div>
                        <div className="space-y-1">
                          {watchlists[k.profile_id].slice(0, 4).map((v) => (
                            <div key={v.video_id} className="flex items-center gap-2 text-xs text-slate-600">
                              <img src={v.thumbnail} alt="" className="h-6 w-9 rounded object-cover" />
                              <span className="truncate flex-1">{v.title}</span>
                              {v.locked && <span className="text-amber-500">🔒</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {!kids.length && <p className="text-sm text-slate-400">No kid profiles yet.</p>}
            </div>
          </Card>

          {/* Order history */}
          <Card className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-4"><Package className="h-5 w-5 text-[#FF5A79]" /><h2 className="text-xl font-display font-bold text-slate-900">Order history</h2></div>
            <div className="grid md:grid-cols-2 gap-6" data-testid="order-history">
              <div>
                <div className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-3"><ArrowDownLeft className="h-4 w-4 text-emerald-500" /> Purchases</div>
                <div className="space-y-2" data-testid="purchases-list">
                  {orders.purchases.length === 0 && <p className="text-sm text-slate-400">No purchases yet. Completed buys appear here.</p>}
                  {orders.purchases.map((p) => (
                    <div key={p.session_id} className="flex items-center gap-3 rounded-2xl bg-slate-50 p-3">
                      {p.listing_image && <img src={p.listing_image} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 text-sm truncate">{p.listing_title}</div>
                        <div className="text-xs text-slate-400">from {p.seller_name || "seller"}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-slate-800">${Number(p.amount).toFixed(2)}</div>
                        <span className={`text-xs font-bold ${p.payment_status === "paid" ? "text-emerald-500" : "text-amber-500"}`}>{p.payment_status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-3"><ArrowUpRight className="h-4 w-4 text-[#FF5A79]" /> Sales</div>
                <div className="space-y-2" data-testid="sales-list">
                  {orders.sales.length === 0 && <p className="text-sm text-slate-400">No sales yet. Items you sell appear here once bought.</p>}
                  {orders.sales.map((s) => (
                    <div key={s.listing_id} className="flex items-center gap-3 rounded-2xl bg-slate-50 p-3">
                      {s.image && <img src={s.image} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 text-sm truncate">{s.title}</div>
                        <div className="text-xs text-slate-400">{s.category} · {s.condition}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-slate-800">${Number(s.price).toFixed(2)}</div>
                        <span className="text-xs font-bold text-emerald-500">sold</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <SubscriptionPlans open={plansOpen} onClose={() => setPlansOpen(false)} current={sub.subscription} onChanged={() => { loadSub(); refreshSubscription(); }} />
    </div>
  );
}
